import React, { useState, useRef, useEffect } from 'react';
import Modal from './Modal';
import ClosingX from './ClosingX';
import BButton from './BButton.mjs';
import TagSelector from './TagSelector.jsx';
import Chip from './common/Chip';
import UploadProgressPanel from './upload/UploadProgressPanel.jsx';
import * as FileService from '../services/FileService.mjs';
import { createTag, getAllAvailableTags } from '../services/TagService.mjs';
import { useUploadManager } from '../context/UploadManagerContext';
import { notify } from '../notifications/errorBus';

import '../css/UploadFileModal.css';
import MetadataEditor from './MetadataEditor';

const UploadFileModal = ({ onClose, initialFiles }) => {
    // --- State ---
    const [fileQueue, setFileQueue] = useState([]); // selection list (pre-upload)
    const [isDragOver, setIsDragOver] = useState(false);
    const [allTagsMap, setAllTagsMap] = useState({});
    const [enableTranscription, setEnableTranscription] = useState(false);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [metadata, setMetadata] = useState({});

    // Execution is handed to the global UploadManager so it survives this modal
    // closing / navigation. Once started we show the live panel for the session.
    const { startQuickUpload } = useUploadManager();
    const [sessionId, setSessionId] = useState(null);

    const fileInputRef = useRef(null);

    // Whole-browser drag-and-drop seeds the queue via initialFiles. The ref guard
    // ensures the dropped batch is enqueued exactly once on mount.
    const initialFilesHandledRef = useRef(false);
    useEffect(() => {
        if (initialFilesHandledRef.current) return;
        initialFilesHandledRef.current = true;
        if (initialFiles && initialFiles.length > 0) {
            handleFileChange(initialFiles);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Lifecycle ---
    useEffect(() => {
        const loadTags = async () => {
            try {
                const tags = await getAllAvailableTags();
                // Keep an id→name map for rendering the selected-tag chips. The
                // TagSelector dropdown fetches and sorts its own list internally.
                const map = {};
                tags.forEach((t) => {
                    map[String(t.id)] = t.name;
                });
                setAllTagsMap(map);
            } catch (err) {
                console.error(err);
            }
        };
        loadTags();
    }, []);

    // --- Helpers ---
    const getSignature = (file) => `${file.name}-${file.size}-${file.lastModified}`;

    const calculateFileHash = async (file) => {
        if (file.size > 1024 * 1024 * 500) {
            // 500MB limit for browser hashing
            console.warn('File too large for client-side hashing, skipping check.');
            return null;
        }
        const buffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    };

    const handleFileChange = (newFiles) => {
        if (!newFiles || newFiles.length === 0) return;
        const newQueueItems = Array.from(newFiles).map((f) => ({
            fileObj: f,
            id: crypto.randomUUID(),
            signature: getSignature(f),
            isDuplicate: false,
            isChecking: true,
        }));

        setFileQueue((prev) => {
            const existingSignatures = new Set(prev.map((item) => item.signature));
            const filtered = newQueueItems.filter((item) => {
                if (existingSignatures.has(item.signature)) return false;
                existingSignatures.add(item.signature);
                return true;
            });
            return [...prev, ...filtered];
        });

        // Opt-in duplicate hint (doesn't block the upload).
        newQueueItems.forEach(async (item) => {
            let exists = false;
            try {
                const hash = await calculateFileHash(item.fileObj);
                exists = await FileService.checkFileExists(
                    item.fileObj.name,
                    item.fileObj.size,
                    hash
                );
            } catch (err) {
                console.error('Hash check failed', err);
            }
            setFileQueue((prev) =>
                prev.map((f) =>
                    f.id === item.id ? { ...f, isDuplicate: exists, isChecking: false } : f
                )
            );
        });
    };

    const removeFile = (id) => setFileQueue((prev) => prev.filter((f) => f.id !== id));

    const startUpload = () => {
        if (fileQueue.length === 0) return;
        const id = startQuickUpload({
            files: fileQueue.map((q) => q.fileObj),
            transcribe: enableTranscription,
            metadata,
            tags: selectedTagIds,
            title: `Uploading ${fileQueue.length} file${fileQueue.length === 1 ? '' : 's'}`,
        });
        setSessionId(id);
    };

    // --- Tag Handlers ---
    const handleExistingTagAdded = (id) => {
        if (!selectedTagIds.includes(id)) setSelectedTagIds((p) => [...p, id]);
    };

    const handleNewTagAdded = async (name) => {
        try {
            const response = await createTag(name);
            let newId = null;
            if (response && typeof response === 'object') newId = String(response.id);
            else if (response) newId = String(response);
            if (newId) {
                // Record the name for the selected-tag chip and mark it selected.
                // TagSelector keeps its own dropdown list; the new tag is excluded
                // there anyway via initialTags={selectedTagIds}.
                setAllTagsMap((prev) => ({ ...prev, [newId]: name }));
                setSelectedTagIds((prev) => [...prev, newId]);
            }
        } catch (e) {
            console.error('Error creating tag:', e);
        }
    };

    const handleRemoveTag = (id) => setSelectedTagIds((p) => p.filter((t) => t !== id));

    // --- Metadata Handlers ---
    const handleMetadataSave = (key, value) => {
        setMetadata((prev) => ({ ...prev, [key]: value }));
        return Promise.resolve();
    };
    const handleMetadataRemove = (key) => {
        setMetadata((prev) => {
            const newMeta = { ...prev };
            delete newMeta[key];
            return newMeta;
        });
        return Promise.resolve();
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const dt = e.dataTransfer;
        // This quick-upload modal can't read a directory (its hash check throws
        // "operation was aborted"). Folders belong in the Import wizard.
        const hasDirectory =
            dt.items &&
            Array.from(dt.items).some((it) => {
                const entry = it.webkitGetAsEntry && it.webkitGetAsEntry();
                return entry && entry.isDirectory;
            });
        if (hasDirectory) {
            notify({
                type: 'info',
                title: 'Folders use Import',
                message:
                    'Drop the folder onto the file browser (not this dialog) to import it with tags.',
            });
            return;
        }
        if (dt.files && dt.files.length > 0) handleFileChange(dt.files);
    };
    const onDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    // ── Once started, the modal is just a live progress panel (Minimize keeps it
    // running in the tray). ───────────────────────────────────────────────────
    if (sessionId) {
        return (
            <Modal className="uploadFileModal modal--chrome" onClose={onClose} aria-labelledby="upload-modal-title">
                <div className="modal-header">
                    <h1 id="upload-modal-title">Upload Files</h1>
                    <ClosingX onClick={onClose} />
                </div>
                <div className="modal-body">
                    <UploadProgressPanel sessionId={sessionId} onClose={onClose} />
                </div>
            </Modal>
        );
    }

    return (
        <Modal className="uploadFileModal" onClose={onClose}>
            <div className="modal-header">
                <h1 id="upload-modal-title">Upload Files</h1>
                <ClosingX onClick={onClose} />
            </div>

            <div className="modal-body">
                <div
                    className={`drop-zone ${isDragOver ? 'dragging' : ''}`}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onClick={() => fileInputRef.current.click()}
                >
                    <div className="dz-content">
                        <span className="dz-icon">📂</span>
                        <p>Drag & Drop files here or click to browse</p>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            handleFileChange(e.target.files);
                            // Reset so re-picking the SAME file fires change
                            // again — otherwise removing a file from the queue
                            // and re-choosing it appeared to do nothing. The
                            // File objects are already captured, so clearing
                            // the input's value doesn't affect them.
                            e.target.value = '';
                        }}
                    />
                </div>

                {fileQueue.length > 0 && (
                    <div className="upload-queue-list">
                        <h4>Files ({fileQueue.length})</h4>
                        {fileQueue.map((item) => (
                            <div
                                key={item.id}
                                className={`queue-item ${item.isDuplicate ? 'is-duplicate' : ''}`}
                            >
                                <div className="qi-content">
                                    <div className="qi-header">
                                        <span className="qi-name" title={item.fileObj.name}>
                                            {item.fileObj.name}
                                        </span>
                                        {item.isChecking && (
                                            <span className="qi-status-label">Checking…</span>
                                        )}
                                        {item.isDuplicate && (
                                            <Chip variant="warning" size="sm">
                                                Exists
                                            </Chip>
                                        )}
                                    </div>
                                </div>
                                <div className="qi-actions">
                                    <button
                                        className="qi-btn remove"
                                        onClick={() => removeFile(item.id)}
                                        aria-label="Remove"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="divider"></div>

                <div className="config-section">
                    <h3>Configuration</h3>
                    <div className="config-row">
                        <label className="checkbox-container">
                            <input
                                type="checkbox"
                                checked={enableTranscription}
                                onChange={(e) => setEnableTranscription(e.target.checked)}
                            />
                            <span className="checkmark"></span>
                            Enable Automatic Transcription
                        </label>
                    </div>
                    <div className="config-row">
                        <h4>Tags</h4>
                        <div className="tag-config-wrapper">
                            <TagSelector
                                initialTags={selectedTagIds}
                                onExistingTagAdded={handleExistingTagAdded}
                                onNewTagAdded={handleNewTagAdded}
                            />
                            <div className="selected-tags-container">
                                {selectedTagIds.map((id) => (
                                    <Chip
                                        key={id}
                                        variant="accent-subtle"
                                        onRemove={() => handleRemoveTag(id)}
                                        removeLabel={`Remove ${allTagsMap[id] || 'tag'}`}
                                    >
                                        {allTagsMap[id] || 'Loading...'}
                                    </Chip>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="config-row">
                        <h4>Metadata Properties</h4>
                        <MetadataEditor
                            metadata={metadata}
                            onSave={handleMetadataSave}
                            onRemove={handleMetadataRemove}
                        />
                    </div>
                </div>
            </div>

            <div className="modal-footer">
                <div className="modal-actions">
                    <BButton onClick={onClose} className="btn-secondary">
                        Close
                    </BButton>
                    <BButton
                        className="btn-primary"
                        onClick={startUpload}
                        enabled={fileQueue.length > 0}
                    >
                        Start Upload
                    </BButton>
                </div>
            </div>
        </Modal>
    );
};

export default UploadFileModal;
